<template>
  <div
    ref="layout-switcher-trigger"
    data-component="layout-switcher"
  >
    <button
      class="layout-switcher-trigger"
      data-button="layout-switcher-trigger"
      @click="onTogglePopup"
    >
      <label
        class="layout-switcher-trigger-label"
        :style="style"
      >
        {{ $t(workspaces[activeWorkspace].name) }}
      </label>

      <or-icon
        :icon="collapseIconType"
        :style="style"
        class="layout-switcher-trigger-icon"
      />
    </button>

    <or-teleport>
      <div
        v-if="isOpen"
        data-popup="layout-switcher-popup"
        :style="{ 'top': popupTop }"
      >
        <div class="layout-switcher-popup-content">
          <layout-switcher-menu
            :workspaces="workspaces"
            :active-workspace="activeWorkspace"
            @link-click="$emit('link-click', $event)"
          />

          <layout-switcher-workspace
            :components="components"
            :workspace="workspaces[activeWorkspace]"
            :default-workspace="defaultWorkspaces[activeWorkspace]"
            @layout-select="onLayoutSelect"
            @layout-bookmark="$emit('layout-bookmark', $event)"
            @layout-reset="$emit('layout-reset', $event)"
            @layout-delete="$emit('layout-delete', $event)"
            @layout-create="$emit('layout-create', $event)"
          />
        </div>

        <div
          class="layout-switcher-popup-overlay"
          @click="onTogglePopup"
        />
      </div>
    </or-teleport>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  toRefs,
  computed,
  getCurrentInstance,
} from 'vue-demi';
import {
  OrIconV3 as OrIcon,
  OrTeleportV3 as OrTeleport,
} from '@onereach/ui-components-25';

import LayoutSwitcherMenu from './LayoutSwitcherMenu.vue';
import LayoutSwitcherWorkspace from './LayoutSwitcherWorkspace.vue';
import { LayoutWorkspace, LayoutComponent } from '@/types';

const POPUP_OFFSET = 8;
export default defineComponent({
  name: 'LayoutSwitcher',
  components: {
    OrIcon,
    OrTeleport,
    LayoutSwitcherMenu,
    LayoutSwitcherWorkspace,
  },
  props: {
    components: {
      type: Object as PropType<Record<string, LayoutComponent>>,
      default: () => new Object(),
    },
    workspaces: {
      type: Object as PropType<Record<string, LayoutWorkspace>>,
      default: () => new Object(),
    },
    defaultWorkspaces: {
      type: Object as PropType<Record<string, LayoutWorkspace>>,
      default: () => new Object(),
    },
    activeWorkspace: {
      type: String,
      default: '',
    },
    triggerColor: {
      type: String,
      default: '',
    },
  },
  emits: [
    'link-click',
    'layout-select',
    'layout-bookmark',
    'layout-reset',
    'layout-delete',
    'layout-create',
  ],
  setup(props) {
    const { activeWorkspace, triggerColor } = toRefs(props);
    const vm = getCurrentInstance()!;

    const isOpen = ref(false);
    const triggerBottom = ref(0);

    const collapseIconType = computed(() => isOpen.value ? 'expand_less' : 'expand_more');
    const popupTop = computed(() => `${triggerBottom.value + POPUP_OFFSET}px`);

    const style = computed(() => {
      return triggerColor.value
        ? { color: `${triggerColor.value} !important` }
        : {};
    });

    const onTogglePopup = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        const triggerRef = vm.proxy.$refs['layout-switcher-trigger'] as HTMLElement;
        triggerBottom.value = triggerRef.getBoundingClientRect().bottom;
      }
    };

    const onClose = (event: MouseEvent) => {
      const triggerRef = vm.proxy.$refs['layout-switcher-trigger'] as HTMLElement;
      const targetElement = event.target as Node;
      if (!triggerRef.contains(targetElement)) {
        isOpen.value = false;
      }
    };

    const onLinkClick = (key: string) => {
      if (key !== activeWorkspace.value) {
        vm.proxy.$emit('link-click', { key });
      }
    };

    const onLayoutSelect = (event: object) => {
      vm.proxy.$emit('layout-select', event);
      onTogglePopup();
    };

    return {
      isOpen,
      collapseIconType,
      popupTop,
      style,
      onTogglePopup,
      onClose,
      onLinkClick,
      onLayoutSelect,
    };
  },
});
</script>

<style lang="scss">
@use '@/styles' as *;
@import '@/modules/app-header/styles';

[data-component='layout-switcher'] {
  .layout-switcher-trigger {
    @include app-header-control-hover();

    display: flex;
    align-items: center;
    gap: var(--s-3);
    height: 2rem;
    max-width: 100%;
    font-size: 1rem;
    font-weight: var(--fw-1);
    background: none;
    border: none;
    cursor: pointer;

    &-label,
    &-icon {
      color: var(--or-c-background);
    }

    &-label {
      @include text-overflow();
      cursor: pointer;
    }

    &-icon {
      font-size: 1rem;
      color: var(--or-c-background);
    }
  }
}

[data-popup='layout-switcher-popup'] {
  position: fixed;
  z-index: 15;
  left: 0;
  right: 0;
  bottom: 0;

  .layout-switcher-popup-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    background-color: rgba(27, 27, 29, 0.8);
  }
}
</style>
